import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  Header,
} from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const RDSComponent = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);
  
  const [rowData, setRowdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');
  const [tenantName, settenantName] = useState<string>(''); // dropdown state
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);
  const identityApi = useApi(identityApiRef)

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        setTenantsFromDb(tenants);
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        if (tenantOrgName) setOrg(tenantOrgName);
        const baseurl = await discoveryApi.getBaseUrl('user');

        let data: any[] = [];
        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/rds-cost-optimization`, {
            method: 'POST',
          });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }

          const response = await fetchresponse.json();
          // response should ideally contain role & data (like your other APIs)
          if (response.role) setRole(response.role);
          data = response.data || response; // support both array or wrapped in { data }

          console.log('Fetched RDS data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }

        const filterData = data.filter((item: any) => item.totalCost !== null);
        setRowdata(filterData);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchData();
    let orgName = localStorage.getItem('orgName')
    if (orgName) {
      setOrg(orgName)
    }
  }, [])

  // Utility function to parse and format recommendations
  function formatRecommendations(value) {
    console.log('Raw recommendations value:', value);

    try {
      // Check if value is a string and parse it
      const recommendations = typeof value === 'string' ? JSON.parse(value) : value;

      console.log('Parsed recommendations:', recommendations);

      // Ensure recommendations is an array and format it
      if (Array.isArray(recommendations)) {
        return recommendations.join(', ');
      }
      return value;
    } catch {
      return value;
    }
  }

  const columnDefs = useMemo(() => {
    const baseCols = [
    {
      headerName: "Tenant Name", field: "tenantName", headerTooltip: "Tenant Name", rowGroup: true
    },
    {
      headerName: "Account Id", field: "accountId", headerTooltip: "Account Id", rowGroup: true, valueFormatter: params => {
        if (params.node.rowIndex === params.api.getDisplayedRowCount() - 1 && !params.value) {
          return 'Total';
        }
        else {
          return params.value;
        }
      }
    },
    { headerName: "Instance Id", field: "instanceId", headerTooltip: "Instance Id" },
    { headerName: "Total Cost in $(per GB per month)", field: "totalCost", headerTooltip: "Total Cost in $(per GB per month)", aggFunc: "sum" },
    { headerName: "Recommendations", field: "recommendations", headerTooltip: "Recommendations", valueFormatter: params => formatRecommendations(params.value) },
    { headerName: "Potential Cost Savings in $(per GB per month)", field: "monthlySavings", headerTooltip: "Potential Cost Savings in $(per GB per month)", aggFunc: "sum" },
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1, filter: true, sortable: true, resizable: true, enableRowGroup: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
    };
  }, []);

  const autoGroupColumnDef = {
    minWidth: 250,
    cellRendererParams: {
      footerValueGetter: (params) => {
        const isRootLevel = params.node.level === -1;
        if (isRootLevel) {
          return "Total";
        }
        return " Sub Total";
      },
    },
  }

  const sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      }
    ],
    defaultToolPanel: '',
  };

  const gridOptions = { suppressContextMenu: true };

  //  Tenant filter logic
  const tenants = [...new Set(rowData.map((r: any) => r.tenantName))];
  const filteredData = tenantName ? rowData.filter(r => String(r.tenantName) === String(tenantName)) : rowData;

  return (
    <div style={containerStyle}>
      <Header title="RDS" subtitle={org} />

      {/* Tenant dropdown for SuperAdmin only */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel></InputLabel>
            <Select
              value={tenantName}
              onChange={(e) => settenantName(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    // force vertical stacking
                    '& .MuiList-root': {
                      display: 'flex !important',
                      flexDirection: 'column !important',
                    },
                    // force left-aligned text inside items
                    '& .MuiMenuItem-root': {
                      justifyContent: 'flex-start',
                      pl: 2,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenantsFromDb.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.name}>
                  {tenant.name}
                </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowGroupPanelShow="always"
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              groupIncludeFooter
              groupIncludeTotalFooter
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div className="scroll-container">
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Downgrade to Smaller Instance</strong>
                  <p>By resizing instances and fine-tuning storage and IOPS according to actual usage, you ensure more efficient and cost-effective RDS management.</p>
                  <ul>
                    <li>Optimize Instance Type: Downgrade to a smaller instance type if CPU and memory usage is consistently low.</li>
                    <li>Adjust Storage and IOPS: Reduce allocated storage and provisioned IOPS if current usage is lower than provisioned levels.</li>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Performance Impact: Ensure that any changes do not negatively affect performance or service availability.</li>
                    <li>Cost Efficiency: Regularly review and adjust settings to optimize cost savings without compromising functionality.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: The potential cost savings for RDS are calculated by assessing opportunities such as downgrading to a smaller instance type if CPU utilization is low, reducing allocated storage if memory usage is low, and adjusting provisioned IOPS if actual usage is lower than provisioned. The potential savings are determined by comparing current costs with optimized costs for these adjustments. The total estimated savings are then subtracted from the overall cost to find the monthly savings.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RDSComponent;